/**
 * AutoresponderRuleModal.tsx
 *
 * Add or edit one automatic reply rule. The fields on offer follow the kind
 * of rule chosen (visibleFields); Save waits until the rule is complete
 * (canSave); a refused save puts each of the server's sentences beside the
 * field it names, and any it cannot place in the banner at the top.
 *
 * On a Locked Admin page (useAdminPageLevel) it is a viewer, and says nothing
 * about it (R8, R18): the fields show their values read-only (not greyed),
 * the title is "Rule", the footer is Close only, nothing submits (not even
 * Enter), and the advice for writing a rule (the template and facility
 * hints, required markers) is left out.
 */
import React, { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { FormField } from '@/components/ui/FormField';
import { SearchableMultiSelect, type SearchableMultiSelectOption } from '@/components/ui/SearchableMultiSelect';
import type { SelectOption } from '@/types/common.types';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import * as autorespondersSvc from '@/services/autoresponders.service';
import type {
  AutoresponderEmailReplyKind,
  AutoresponderEventType,
  AutoresponderNamedChoice,
  AutoresponderOptionGroup,
  AutoresponderOptions,
  AutoresponderRule,
} from '@/services/autoresponders.service';
import {
  COPY,
  EVENT_TYPE_LABELS,
  EMAIL_REPLY_KIND_LABELS,
  buildPayload,
  canSave,
  emptyForm,
  errorMessageFromError,
  facilityNoHoursHint,
  fieldErrorsFromError,
  formFromRule,
  savedFacilities,
  savedLeadSources,
  validateForm,
  visibleFields,
  type RuleForm,
} from './autoresponderForm';

/** Select values cannot be null, so "none" / "any" is 0 for the id pickers. */
const NONE = 0;

function groupedOptions(groups: AutoresponderOptionGroup[], noneLabel?: string): SelectOption<number>[] {
  const options: SelectOption<number>[] = noneLabel ? [{ value: NONE, label: noneLabel }] : [];
  for (const group of groups) {
    for (const item of group.items) {
      options.push({ value: item.id, label: item.name, description: group.categoryName });
    }
  }
  return options;
}

/**
 * Keep a stored choice that is no longer on offer (a template switched off
 * since) visible by name, so opening the editor does not silently blank it.
 */
function withCurrent(
  options: SelectOption<number>[],
  id: number | null,
  name: string | null | undefined,
): SelectOption<number>[] {
  if (id === null || id === NONE || options.some((o) => o.value === id)) return options;
  return [...options, { value: id, label: name ? `${name} (${COPY.templateGone})` : COPY.templateGone }];
}

/**
 * Choices for a multi-pick list, plus every saved one no longer on offer
 * (turned off since), kept by name so it stays visibly chosen.
 */
function multiOptions(
  offered: { id: number; name: string; subtitle?: string }[],
  savedIds: number[],
  saved: AutoresponderNamedChoice[] | undefined,
): SearchableMultiSelectOption[] {
  const options: SearchableMultiSelectOption[] = offered.map((o) => ({
    value: String(o.id),
    label: o.name,
    subtitle: o.subtitle,
  }));
  for (const id of savedIds) {
    if (options.some((o) => o.value === String(id))) continue;
    const name = saved?.find((s) => s.id === id)?.name;
    options.push({
      value: String(id),
      label: name ? `${name} ${COPY.choiceGoneSuffix}` : COPY.choiceGoneSuffix,
    });
  }
  return options;
}

interface Props {
  rule?: AutoresponderRule | null;
  options: AutoresponderOptions;
  onClose: () => void;
  onSaved: (rule: AutoresponderRule) => void;
}

export function AutoresponderRuleModal({ rule, options, onClose, onSaved }: Props) {
  const isEdit = rule != null;
  const locked = useAdminPageLevel() === 'locked';
  const limits = options.repeatWindowHours;

  const [form, setForm] = useState<RuleForm>(() =>
    rule ? formFromRule(rule) : emptyForm(limits.default),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const fields = visibleFields(form.eventType);
  const ready = canSave(form, limits);
  const repeatError = locked ? undefined : validateForm(form, limits).repeatWindowHours;

  function set<K extends keyof RuleForm>(key: K, value: RuleForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    // An edited field's old refusal no longer applies.
    setFieldErrors((errs) => {
      if (!(key in errs)) return errs;
      const next = { ...errs };
      delete next[key as string];
      return next;
    });
  }

  const eventTypeOptions = useMemo<SelectOption<AutoresponderEventType>[]>(
    () =>
      (options.eventTypes.length > 0
        ? options.eventTypes
        : (Object.keys(EVENT_TYPE_LABELS) as AutoresponderEventType[]).map((value) => ({
            value,
            label: EVENT_TYPE_LABELS[value],
          }))),
    [options.eventTypes],
  );

  const practiceNumberOptions = useMemo<SelectOption<string>[]>(() => {
    const list: SelectOption<string>[] = [
      { value: '', label: COPY.practiceNumberAny },
      ...options.practiceNumbers.map((n) => ({ value: n.value, label: n.label })),
    ];
    if (form.practiceNumber !== '' && !list.some((o) => o.value === form.practiceNumber)) {
      list.push({ value: form.practiceNumber, label: rule?.practiceNumberLabel ?? form.practiceNumber });
    }
    return list;
  }, [options.practiceNumbers, form.practiceNumber, rule]);

  const emailReplyKindOptions = useMemo<SelectOption<AutoresponderEmailReplyKind | ''>[]>(
    () => [
      { value: '', label: COPY.emailReplyKindAny },
      ...(options.emailReplyKinds.length > 0
        ? options.emailReplyKinds
        : (Object.keys(EMAIL_REPLY_KIND_LABELS) as AutoresponderEmailReplyKind[]).map((value) => ({
            value,
            label: EMAIL_REPLY_KIND_LABELS[value],
          }))),
    ],
    [options.emailReplyKinds],
  );

  // Options keep what was saved, not what is ticked now, so unticking a
  // turned-off choice does not make it vanish before the admin saves.
  const leadSourceOptions = useMemo(
    () =>
      multiOptions(
        options.leadSources.flatMap((g) => g.items.map((i) => ({ ...i, subtitle: g.categoryName }))),
        rule ? savedLeadSources(rule).map((s) => s.id) : [],
        rule ? savedLeadSources(rule) : undefined,
      ),
    [options.leadSources, rule],
  );
  const planTypeOptions = useMemo(
    () => multiOptions(options.planTypes ?? [], rule?.planTypeIds ?? [], rule?.planTypes),
    [options.planTypes, rule],
  );

  // A lead rule may send either template alone, so each needs a "none" choice.
  const optionalTemplates = form.eventType === 'new_lead';
  const smsOptions = useMemo(
    () =>
      withCurrent(
        groupedOptions(options.smsTemplates, optionalTemplates ? COPY.smsTemplateNone : undefined),
        form.smsTemplateId,
        rule?.smsTemplateName,
      ),
    [options.smsTemplates, optionalTemplates, form.smsTemplateId, rule],
  );
  const emailOptions = useMemo(
    () =>
      withCurrent(
        groupedOptions(options.emailTemplates, optionalTemplates ? COPY.emailTemplateNone : undefined),
        form.emailTemplateId,
        rule?.emailTemplateName,
      ),
    [options.emailTemplates, optionalTemplates, form.emailTemplateId, rule],
  );
  const headerFooterOptions = useMemo(
    () =>
      withCurrent(
        [
          { value: NONE, label: COPY.headerFooterNone },
          ...options.headerFooters.map((h) => ({ value: h.id, label: h.name })),
        ],
        form.emailHeaderFooterId,
        rule?.emailHeaderFooterName,
      ),
    [options.headerFooters, form.emailHeaderFooterId, rule],
  );

  const facilityOptions = useMemo(
    () =>
      multiOptions(
        options.facilities.map((f) => ({
          id: f.id,
          name: f.hasUsableHours ? f.name : `${f.name} ${COPY.facilityNoHoursSuffix}`,
        })),
        rule ? savedFacilities(rule).map((f) => f.id) : [],
        rule ? savedFacilities(rule) : undefined,
      ),
    [options.facilities, rule],
  );
  // Only offered facilities are known to lack hours; one no longer offered is
  // already marked as such in the list.
  const chosenWithoutHours = options.facilities
    .filter((f) => !f.hasUsableHours && form.officeHoursFacilityIds.includes(f.id))
    .map((f) => f.name);
  // A chosen facility no longer offered (turned off or gone) is left out of
  // the office-hours check, so it cannot be "the other facility" that decides.
  const chosenOffered = options.facilities.filter((f) => form.officeHoursFacilityIds.includes(f.id)).length;
  const facilityHint = form.outsideOfficeHoursOnly && !locked
    ? facilityNoHoursHint(chosenWithoutHours, chosenOffered)
    : '';

  /** Sentences the server sent for a field this form is not showing. */
  const unplacedErrors = Object.entries(fieldErrors)
    .filter(([key]) => !isShown(key))
    .map(([, message]) => message);

  function isShown(key: string): boolean {
    switch (key) {
      case 'name':
      case 'eventType':
      case 'outsideOfficeHoursOnly':
      case 'repeatWindowHours':
      case 'active':
        return true;
      case 'smsTemplateId':
        return fields.smsTemplate;
      case 'emailTemplateId':
        return fields.emailTemplate;
      case 'emailHeaderFooterId':
        return fields.headerFooter;
      case 'practiceNumber':
        return fields.practiceNumber;
      case 'leadSourceIds':
        return fields.leadSource;
      case 'planTypeIds':
        return fields.planType;
      case 'emailReplyKind':
        return fields.emailReplyKind;
      case 'officeHoursFacilityIds':
        return form.outsideOfficeHoursOnly;
      default:
        return false;
    }
  }

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!ready || saving || locked) return;

    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      const payload = buildPayload(form);
      const saved = rule
        ? await autorespondersSvc.updateAutoresponderRule(rule.id, payload)
        : await autorespondersSvc.createAutoresponderRule(payload);
      onSaved(saved);
      onClose();
    } catch (err) {
      setFieldErrors(fieldErrorsFromError(err));
      setError(errorMessageFromError(err, COPY.saveError));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={locked ? COPY.viewRule : isEdit ? COPY.editRule : COPY.addRule}
      size="lg"
      footer={locked ? (
        <Button variant="secondary" onClick={onClose}>
          {COPY.close}
        </Button>
      ) : (
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {COPY.cancel}
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={saving || !ready} loading={saving}>
            {isEdit ? COPY.saveChanges : COPY.addRule}
          </Button>
        </>
      )}
    >
      <AdminLockedFieldset>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              <p>{error}</p>
              {unplacedErrors.length > 0 && (
                <ul className="mt-1 list-disc pl-5">
                  {unplacedErrors.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {rule && rule.warnings.length > 0 && (
            <ul className="space-y-1 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {rule.warnings.map((w, i) => (
                <li key={`${w.code}-${i}`} className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  <span>{w.message}</span>
                </li>
              ))}
            </ul>
          )}

          <FormField label={COPY.nameLabel} required={!locked} htmlFor="autoresponder-name" error={fieldErrors.name}>
            <Input
              id="autoresponder-name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder={COPY.namePlaceholder}
              required
              autoFocus
            />
          </FormField>

          <Select<AutoresponderEventType>
            id="autoresponder-event-type"
            label={COPY.eventTypeLabel}
            required={!locked}
            placeholder={COPY.eventTypePlaceholder}
            options={eventTypeOptions}
            value={form.eventType === '' ? null : form.eventType}
            onChange={(v) => set('eventType', v ?? '')}
            error={fieldErrors.eventType}
          />

          {fields.practiceNumber && (
            <Select<string>
              id="autoresponder-practice-number"
              label={COPY.practiceNumberLabel}
              options={practiceNumberOptions}
              value={form.practiceNumber}
              onChange={(v) => set('practiceNumber', v ?? '')}
              error={fieldErrors.practiceNumber}
            />
          )}

          {fields.leadSource && (
            <FormField label={COPY.leadSourceLabel} htmlFor="autoresponder-lead-sources" error={fieldErrors.leadSourceIds}>
              <SearchableMultiSelect
                id="autoresponder-lead-sources"
                options={leadSourceOptions}
                selectedValues={form.leadSourceIds.map(String)}
                onSelectionChange={(ids) => set('leadSourceIds', ids.map(Number))}
                placeholder={COPY.leadSourceAny}
                searchPlaceholder={COPY.leadSourceSearch}
              />
            </FormField>
          )}

          {fields.planType && (
            <FormField label={COPY.planTypeLabel} htmlFor="autoresponder-plan-types" error={fieldErrors.planTypeIds}>
              <SearchableMultiSelect
                id="autoresponder-plan-types"
                options={planTypeOptions}
                selectedValues={form.planTypeIds.map(String)}
                onSelectionChange={(ids) => set('planTypeIds', ids.map(Number))}
                placeholder={COPY.planTypeAny}
                searchPlaceholder={COPY.planTypeSearch}
              />
            </FormField>
          )}

          {fields.emailReplyKind && (
            <Select<AutoresponderEmailReplyKind | ''>
              id="autoresponder-email-reply-kind"
              label={COPY.emailReplyKindLabel}
              options={emailReplyKindOptions}
              value={form.emailReplyKind}
              onChange={(v) => set('emailReplyKind', v ?? '')}
              error={fieldErrors.emailReplyKind}
            />
          )}

          {(fields.smsTemplate || fields.emailTemplate) && (
            <div className="space-y-4">
              {fields.smsTemplate && (
                <Select<number>
                  id="autoresponder-sms-template"
                  label={COPY.smsTemplateLabel}
                  required={!locked && form.eventType === 'inbound_sms'}
                  searchable
                  placeholder={COPY.templatePlaceholder}
                  options={smsOptions}
                  value={form.smsTemplateId ?? (optionalTemplates ? NONE : null)}
                  onChange={(v) => set('smsTemplateId', v && v !== NONE ? v : null)}
                  error={fieldErrors.smsTemplateId}
                />
              )}

              {fields.emailTemplate && (
                <Select<number>
                  id="autoresponder-email-template"
                  label={COPY.emailTemplateLabel}
                  required={!locked && form.eventType === 'email_reply'}
                  searchable
                  placeholder={COPY.templatePlaceholder}
                  options={emailOptions}
                  value={form.emailTemplateId ?? (optionalTemplates ? NONE : null)}
                  onChange={(v) => set('emailTemplateId', v && v !== NONE ? v : null)}
                  error={fieldErrors.emailTemplateId}
                />
              )}

              {fields.headerFooter && (
                <Select<number>
                  id="autoresponder-header-footer"
                  label={COPY.headerFooterLabel}
                  options={headerFooterOptions}
                  value={form.emailHeaderFooterId ?? NONE}
                  onChange={(v) => set('emailHeaderFooterId', v && v !== NONE ? v : null)}
                  disabled={form.emailTemplateId === null}
                  error={fieldErrors.emailHeaderFooterId}
                />
              )}

              {!locked && <p className="text-xs text-slate-500">{COPY.templateHint}</p>}
            </div>
          )}

          <div className="space-y-3">
            <Checkbox
              checked={form.outsideOfficeHoursOnly}
              onChange={(e) => set('outsideOfficeHoursOnly', e.target.checked)}
              label={COPY.afterHoursLabel}
              error={fieldErrors.outsideOfficeHoursOnly}
            />

            {form.outsideOfficeHoursOnly && (
              <FormField
                label={COPY.facilityLabel}
                required={!locked}
                htmlFor="autoresponder-facilities"
                error={fieldErrors.officeHoursFacilityIds}
                helpText={facilityHint || undefined}
              >
                <SearchableMultiSelect
                  id="autoresponder-facilities"
                  options={facilityOptions}
                  selectedValues={form.officeHoursFacilityIds.map(String)}
                  onSelectionChange={(ids) => set('officeHoursFacilityIds', ids.map(Number))}
                  placeholder={COPY.facilityPlaceholder}
                  searchPlaceholder={COPY.facilitySearch}
                />
              </FormField>
            )}
          </div>

          <FormField
            label={COPY.repeatLabel}
            htmlFor="autoresponder-repeat"
            error={fieldErrors.repeatWindowHours ?? repeatError}
          >
            <Input
              id="autoresponder-repeat"
              type="number"
              inputMode="numeric"
              min={limits.min}
              max={limits.max}
              step={1}
              value={form.repeatWindowHours}
              onChange={(e) => set('repeatWindowHours', e.target.value)}
              className="max-w-[8rem]"
            />
          </FormField>

          <Checkbox
            checked={form.active}
            onChange={(e) => set('active', e.target.checked)}
            label={COPY.activeLabel}
          />
        </form>
      </AdminLockedFieldset>
    </Modal>
  );
}
